
import React from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';

// Props for InvoiceCategoryTabs
interface InvoiceCategoryTabsProps {
  activeCategory: string;
  onCategoryChange: (selectedCategory: {
    name: string; label: string; count?: number 
}) => void;
  categories?: {
   name: string; label: string; count?: number 
}[];
  /** Size variant - 'small' for compact chips, 'medium' (default) for normal size */
  size?: 'small' | 'medium';
}



// Default categories if not provided via props
const defaultInvoiceCategories = [
  { name: 'Patient', label: 'Patient', count: 16 },
  { name: 'Donor', label: 'Donor', count: 10 },
  { name: 'Company', label: 'Company', count: 10 },
  { name: 'Surrogate', label: 'Surrogate', count: 14 },
  { name: 'Cryo-Billing', label: 'Cryo-Billing', count: 12 },
];


/**
 * InvoiceCategoryTabs renders a horizontal tab bar for invoice categories.
 * Highlights the active tab and calls onCategoryChange with the selected category object.
 * If categories are not provided, uses a default set.
 */
const InvoiceCategoryTabs: React.FC<InvoiceCategoryTabsProps> = ({
  activeCategory,
  onCategoryChange,
  categories,
  size = 'medium',
}) => {
  // Use provided categories or fallback to default
  const categoryList = categories && categories.length > 0 ? categories : defaultInvoiceCategories;

  // Find the selected category object (prefer name, fallback to label)
  const selectedCategory = categoryList.find(cat => (cat.name ? cat.name === activeCategory : cat.label === activeCategory)) || categoryList[0];

  // Size-based styles
  const isSmall = size === 'small';

  // Keyboard navigation: arrow keys to cycle through tabs
  const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
    const categoryCount = categoryList.length;
    let nextIndex = index;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      nextIndex = (index + 1) % categoryCount;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      nextIndex = (index - 1 + categoryCount) % categoryCount;
    } else if (e.key === 'Home') {
      e.preventDefault();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      nextIndex = categoryCount - 1;
    }

    if (nextIndex !== index) {
      const nextCategory = categoryList[nextIndex];
      onCategoryChange(nextCategory);
    }
  };

  return (
    <Stack 
      direction="row" 
      spacing={isSmall ? 0.75 : 1}
      role="tablist"
      aria-label="Invoice category filters"
      sx={{ flexWrap: 'nowrap' }}
    >
      {categoryList.map((category, index) => {
        // Determine if this tab is the currently active one
        const isActive = selectedCategory.label === category.label;

        // Enhanced button styles for smoother UI - optimized for laptop screens
        const buttonStyles = {
          border: isActive ? '2px solid #F37A3D' : '1px solid #E0E0E0',
          background: isActive ? '#FFF6F2' : '#fff',
          color: isActive ? '#F37A3D' : '#B0B0B0',
          borderRadius: isSmall ? 2 : 2.5,
          fontWeight: 600,
          fontSize: isSmall ? 11 : { xs: 12, sm: 13, md: 14 },
          minWidth: isSmall ? 70 : { xs: 80, sm: 90, md: 100, lg: 110 },
          padding: isSmall ? '3px 8px' : { xs: '4px 8px', sm: '5px 10px', md: '6px 12px' },
          height: isSmall ? 26 : { xs: 30, sm: 32, md: 34 },
          boxShadow: isActive ? '0 2px 8px rgba(243, 122, 61, 0.08)' : 'none',
          outline: 'none',
          transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: 'scale(1)',
          whiteSpace: 'nowrap',
          '&:hover': {
            background: isActive ? '#FFF6F2' : '#FAFAFA',
            border: isActive ? '2px solid #F37A3D' : '1px solid #B0B0B0',
            color: isActive ? '#F37A3D' : '#F37A3D',
            boxShadow: isActive ? '0 4px 12px rgba(243, 122, 61, 0.12)' : '0 2px 8px rgba(176, 176, 176, 0.08)',
            transform: 'scale(1.02)',
          },
          '&:focus-visible': {
            outline: '2px solid #F37A3D',
            outlineOffset: '2px',
          }
        };

        return (
          <Button
            key={category.label}
            data-testid={`invoice-category-tab-${category.name.toLowerCase().replace(/[\s-]/g, '-')}`}
            onClick={() => onCategoryChange(category)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            variant={isActive ? 'outlined' : 'text'}
            size={isSmall ? 'small' : 'medium'}
            role="tab"
            aria-selected={isActive}
            aria-label={`${category.label}${category.count !== undefined && category.count !== null ? `, ${category.count} items` : ''}`}
            tabIndex={isActive ? 0 : -1}
            sx={buttonStyles}
          >
            {category.label}{category.count !== undefined && category.count !== null ? ` (${category.count})` : ''}
          </Button>
        );
      })}
    </Stack>
  );
};

export default InvoiceCategoryTabs;

